import { components } from 'api/goodtrust/swagger'
import { useUserMe } from 'api/goodtrust/user'
import {
  deleteUserDirective,
  putUserDirectiveContact,
  useUserTrust,
} from 'api/goodtrust/user/directive'
import { Button } from 'components/button/Button'
import { ContactShareItem } from 'components/contact/contactShareForm/ContactShareForm'
import {
  ContactShareInvite,
  parseSharedInvitedContacts,
} from 'components/contact/contactShareInvite/ContactShareInvite'
import { useDirectivePaywallModal } from 'components/content/directive/directivePaywallModal/DirectivePaywallModal'
import { getDirectiveSpec } from 'components/directive/utils/directiveSpecs'
import {
  calculateDirectiveProgress,
  useDirectiveSteps,
} from 'components/directive/utils/directiveUtils'
import { Icon } from 'components/icon'
import { ConfirmModal } from 'components/modal/confirmModal/ConfirmModal'
import { useModal } from 'components/modal/Modal'
import { usePopperModal } from 'components/modal/PopperModal'
import { handleAndToastError, toastSuccess } from 'components/Toast'
import { WizardGridCard } from 'components/wizard/wizardGridCard/WizardGridCard'
import { extractDirectiveName } from 'logic/directive/extractDirectiveName'
import { getDirectiveDoneLink, getDirectiveEditLink } from 'logic/directive/links'
import { useTranslation } from 'next-i18next'
import Router from 'next/router'
import { colors } from 'utils/colors'
import { ApiError } from 'utils/error'
import { unwrapResponseBlob } from 'utils/fetcher'
import { openBlob } from 'utils/file'
import { formatUpdatedAgo } from 'utils/format'
import { getReadableString } from 'utils/general'
import { useLoadingCallback } from 'utils/hooks'
import { useDynamicTranslations } from 'utils/i18n/useDynamicTranslations'
import { describeMyPlans } from 'logic/subscription/plan/my/describe'
import { useSimpleDirectiveSteps } from 'components/directive/simpleDirectiveLayout/SimpleDirectiveLayout'

const useTitle = (directive: components['schemas']['UserDirectiveResponse']) => {
  const { useTranslatedNames } = getDirectiveSpec(directive.directive_type)
  const names = useTranslatedNames()
  const isTrust = directive.directive_type === 'TRUST'
  const trust = useUserTrust({ params: { uuid: isTrust ? directive.uuid : undefined } })

  const directiveName =
    trust.data?.json && extractDirectiveName(directive.directive_type, trust.data?.json)

  if (directiveName) return directiveName
  return `${names.short} ${Number(directive.numbering) > 1 ? `#${directive.numbering}` : ''}`
}

const getBrief = (directive: components['schemas']['UserDirectiveResponse'], isOwn?: boolean) => {
  if (isOwn) {
    return directive.modified ? formatUpdatedAgo(directive.modified) : ''
  }

  if (directive.user?.first_name) {
    return `${directive.user.first_name} ${directive.user.last_name}`
  }

  return directive.user?.email
}

// Handled with GT-4404
export function DirectiveCard(props: {
  directive: components['schemas']['UserDirectiveResponse']
  onDataChange?: () => void
}) {
  const { t } = useDynamicTranslations('trusted_contact')
  const { t: tCommon } = useTranslation()
  const { directive } = props
  const { directive_type } = directive
  const { data: user } = useUserMe()
  const isOwner = user?.json?.email === directive.user?.email
  const isCompleted = directive.state === 'COMPLETED'

  const userMe = user?.json
  const hasFullDirectiveAccess = userMe ? describeMyPlans(userMe).hasFullDirectiveAccess() : false
  const isSimpleFlow = directive_type === 'WILL' || directive_type === 'TRUST'
  const calculateForSimpleFlow = isSimpleFlow && !hasFullDirectiveAccess

  const editLink = getDirectiveEditLink(directive, user?.json ?? {})
  const doneLink = getDirectiveDoneLink(directive.uuid, directive.directive_type)

  const spec = getDirectiveSpec(directive.directive_type)

  const [handleShare, isSharing] = useLoadingCallback(
    async (contactShareData: ContactShareItem[]) => {
      try {
        if (directive?.uuid == null) throw Error()
        const res = await putUserDirectiveContact(
          {
            share:
              contactShareData?.map((contact) => ({
                trusted_contact_uuid: contact.uuid,
                access_type: 'READ_ONLY',
                share_mode: contact.share_mode || 'NOW',
              })) ?? [],
          },
          { uuid: directive.uuid }
        )
        if (!res.ok) throw Error()
        toastSuccess('directive_shared')
        closeMenu()
        await props.onDataChange?.()
      } catch (err) {
        handleAndToastError(err, 'failed_to_share_directive')
      }
    }
  )

  const [deleteModal, showDeleteModal] = useModal(({ close, isOpen }) =>
    isOpen ? (
      <ConfirmModal
        title={t('trusted_contact.directive.confirm_title')}
        danger
        onConfirm={async () => {
          try {
            if (directive?.uuid == null) throw Error()
            const res = await deleteUserDirective(undefined, { uuid: directive.uuid })
            if (!res.ok) throw Error(res?.error?.toString?.())
            toastSuccess('directive_deleted')
            close()
            props.onDataChange?.()
          } catch (err) {
            handleAndToastError(err, 'failed_to_delete_directive')
          }
        }}
        close={close}
      />
    ) : null
  )

  const [inviteModal, showInviteModal] = useModal(
    ({ close, isOpen }) =>
      isOpen ? (
        <ContactShareInvite
          hideAccessType
          defaultValue={parseSharedInvitedContacts(directive.trusted_contacts)}
          onSubmit={(data) => {
            handleShare(data)
            close()
          }}
        />
      ) : null,
    {
      disableFocusLock: true,
      modalContentCss: { overflowY: 'visible' },
      mobilePlacement: 'top',
    }
  )

  const [upgradeModal, showUpgradeModal] = useDirectivePaywallModal({
    eventArgs: {
      dir_type: getDirectiveSpec(directive.directive_type).analyticsDirectiveType,
      place: 'inside_page',
    },
    isOwner,
    ownerName: directive.user?.first_name || directive.user?.email,
  })

  const [downloadDirective, isDownloading] = useLoadingCallback(async () => {
    try {
      if (!directive.uuid) throw Error()
      closeMenu()
      let res
      if (directive.eligible_download) {
        const spec = getDirectiveSpec(directive.directive_type)
        spec.fireAnalyticsEvent.downloadPdf()
        res = await spec.downloadPdf(directive.uuid).then(unwrapResponseBlob)

        if (!res.blob) throw new ApiError(res)
        else await openBlob(res.blob, `${getReadableString(directive.directive_type)}.pdf`)
      } else {
        await showUpgradeModal()
      }
    } catch (err) {
      handleAndToastError(err, 'failed_to_download_directive')
    }
  })

  const {
    modal: menu,
    setReferenceElement,
    open: openMenu,
    close: closeMenu,
  } = usePopperModal(
    () => (
      <>
        {directive?.state === 'COMPLETED' && (
          <Button
            type="button"
            variant="text"
            onClick={downloadDirective}
            leftIcon={<Icon name="FileDownloadFrame" />}
          >
            {t('trusted_contact.directive.download_pdf_button')}
          </Button>
        )}
        <Button
          type="button"
          variant="text"
          onClick={() => void showInviteModal()}
          leftIcon={<Icon name="Share" />}
        >
          {t('trusted_contact.directive.share_button')}
        </Button>
        <Button
          type="button"
          variant="text"
          onClick={() => void Router.push(editLink)}
          leftIcon={<Icon name="DocumentEdit" />}
        >
          {directive?.state === 'COMPLETED'
            ? t('trusted_contact.directive.edit_button')
            : t('trusted_contact.directive.continue_editing_button')}
        </Button>
        <Button
          type="button"
          variant="text"
          tone="danger"
          onClick={showDeleteModal}
          leftIcon={<Icon name="Bin" />}
        >
          {t('trusted_contact.directive.delete_button')}
        </Button>
      </>
    ),
    { placement: 'bottom-end' }
  )

  const steps = calculateForSimpleFlow
    ? useSimpleDirectiveSteps(directive_type)
    : useDirectiveSteps(spec.sections, directive?.last_section || '', directive)

  const progress = calculateDirectiveProgress(steps, true)

  return (
    <>
      {deleteModal}
      {inviteModal}
      {upgradeModal}
      {menu}
      <WizardGridCard
        ref={setReferenceElement}
        image={<Icon name={spec.icon} color={colors.gray[500]} />}
        data-testid={`directive-item-${directive.uuid}`}
        isLoading={isSharing || isDownloading}
        openMenu={isOwner ? openMenu : undefined}
        locked={directive?.locked}
        onClick={() =>
          void Router.push(directive?.state === 'COMPLETED' || !isOwner ? doneLink : editLink)
        }
        title={useTitle(directive)}
        brief={getBrief(directive, isOwner)}
        entityType={directive.state !== 'COMPLETED' && tCommon('common.draft')}
        status={{
          label: isCompleted
            ? t('trusted_contact.directive.completed_status')
            : t('trusted_contact.directive.percent_completed_status', {
                percent: (progress * 100).toFixed(0),
              }),
          variant: isCompleted ? 'success' : 'warning',
        }}
      />
    </>
  )
}
